import React, { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  en: {
    // Common
    app_name: 'CitrusQC',
    app_subtitle: 'Fresh Produce Quality Control',
    loading: 'Loading...',
    save: 'Save',
    cancel: 'Cancel',
    create: 'Create',
    update: 'Update',
    edit: 'Edit',
    delete: 'Delete',
    deactivate: 'Deactivate',
    confirm_delete: 'Are you sure?',
    success: 'Success!',
    error: 'Error',
    yes: 'Yes',
    no: 'No',
    search: 'Search',
    filter: 'Filter',
    apply: 'Apply',
    export_csv: 'CSV',
    export_pdf: 'PDF',
    previous: 'Previous',
    next: 'Next',
    page_of: 'of',
    active: 'Active',
    inactive: 'Inactive',
    status: 'Status',
    actions: 'Actions',
    created: 'Created',
    date: 'Date',
    all: 'All',

    // Auth
    login: 'Sign In',
    logout: 'Logout',
    username: 'Username',
    password: 'Password',
    enter_username: 'Enter your username',
    enter_password: 'Enter your password',
    logging_in: 'Logging in...',
    login_failed: 'Login failed. Please try again.',
    invalid_credentials: 'Invalid credentials.',

    // Nav
    nav_inspect: 'Inspect',
    nav_history: 'History',
    nav_packer_report: 'Packers',
    nav_reports: 'Reports',
    nav_settings: 'Settings',

    // Dashboard / Inspection
    box_inspection: 'Box Inspection',
    inspector: 'Inspector',
    packager: 'Packager',
    select_packager: 'Select packager...',
    product: 'Product',
    select_product: 'Select product...',
    minor_defects: 'Minor Defects',
    major_defects: 'Major Defects',
    minor_pct: 'Minor %',
    major_pct: 'Major %',
    total_pct: 'Total %',
    notes: 'Notes',
    notes_placeholder: 'Describe any defects found...',
    photos: 'Photos',
    photos_limit: 'up to 5',
    submit_inspection: 'Submit Inspection',
    submitting: 'Submitting...',
    box_inspected: 'Box Inspected!',
    inspection_saved: 'Inspection saved successfully',
    critical_defects: 'Critical Defects',
    critical_pct: 'Critical %',
    critical_desc: 'Decayed / Spoiled',
    minor_desc: 'Shape, colour, skin marks',
    major_desc: 'Pest, frost, injuries',
    fruit: 'Fruit',
    size_code: 'Size Code',
    diameter: 'Diameter',
    pieces_per_box: 'Pieces / Box',
    quality_class: 'Class',
    total_pieces: 'Total Pieces',
    total_defect: 'Total Defect %',

    // Barcode Scanner
    scan_barcode: 'Scan Barcode',
    scanning: 'Scanning...',
    point_camera: 'Point camera at packager barcode',
    stop_scan: 'Stop Scan',
    or_select_manually: 'or select manually',
    packager_not_found: 'Packager not found',
    packager_not_found_msg: 'Barcode "{code}" is not registered. Would you like to create a new packager?',
    create_new_packager: 'Create New Packager',
    packager_name: 'Packager Name',
    packager_number: 'Packager Number',
    enter_packager_name: 'Enter packager name',
    packager_created: 'Packager created!',
    camera_error: 'Could not access camera. Please check permissions.',

    // My Inspections
    my_inspections: 'My Inspections',
    no_inspections: 'No inspections yet',
    no_inspections_desc: 'Start inspecting boxes from the dashboard!',
    delete_inspection: 'Delete Inspection',
    delete_inspection_confirm: 'Delete this inspection and its photos? This cannot be undone.',

    // Reports
    reports_title: 'Reports & Analytics',
    crunching: 'Crunching the numbers...',
    total_boxes: 'Total Boxes',
    avg_minor: 'Avg Minor %',
    avg_major: 'Avg Major %',
    avg_critical: 'Avg Critical %',
    critical_found: 'Critical Found',
    critical_warning: 'CRITICAL: Decayed/Spoiled fruit detected!',
    packer_report_title: 'Packer Performance',
    packer_report_hint: 'Tap a packer to see their recent inspections from all inspectors',
    recent_inspections_for: 'Recent Inspections for',
    all_inspectors_note: 'Showing inspections from all inspectors',
    total_defects: 'Total Defects',
    fruits_inspected: 'Fruits Inspected',
    boxes_per_inspector: 'Boxes per Inspector',
    major_defects_by_fruit: 'Major Defects by Fruit',
    product_distribution: 'Product Distribution',
    all_products: 'All Products',
    defect_trends: 'Defect Trends',
    packager_performance: 'Packager Performance',
    nav_overview: 'Overview',
    nav_charts: 'Charts',
    nav_packagers: 'Packagers',
    nav_inspectors: 'Inspectors',
    prev: 'Prev',
    next: 'Next',
    packer_history_title: 'Packer Performance History',
    packer_history_prompt: 'Select a packer to view their daily performance history',
    daily_performance: 'Daily Performance',
    inspector_performance: 'Inspector Performance',
    boxes: 'Boxes',
    name: 'Name',
    rating: 'Rating',
    minor_found: 'Minor Found',
    major_found: 'Major Found',
    from_date: 'From',
    to_date: 'To',
    period_1d: '1 Day',
    period_1w: '1 Week',
    period_1m: '1 Month',
    all_time: 'All Time',
    top_packager: 'Top Performing Packager',
    worst_packager: 'Worst Packager',
    least_active_inspector: 'Least Active Inspector',
    top_inspector: 'Top Quality Controller',
    quality_controller: 'Quality Controller',

    // Settings
    settings_title: 'Settings',
    products_tab: 'Products',
    users_tab: 'Users',
    packagers_tab: 'Packagers',
    product_catalog: 'Product Catalog',
    add_product: '+ Add Product',
    add_user: '+ Add User',
    add_packager: '+ Add Packager',
    new_product: 'New Product',
    edit_product: 'Edit Product',
    new_user: 'New User',
    edit_user: 'Edit User',
    new_packager: 'New Packager',
    edit_packager: 'Edit Packager',
    product_code: 'Product Code',
    fruit_type: 'Fruit Type',
    diameter_range: 'Diameter Range',
    description: 'Description',
    minor_tolerance: 'Minor Tolerance %',
    major_tolerance: 'Major Tolerance %',
    critical_tolerance: 'Critical Tolerance %',
    tolerance_settings: 'Defect Tolerances (OECD)',
    role: 'Role',
    new_password: 'New password (optional)',
    deactivate_confirm: 'Deactivate this product? It will no longer be available for new inspections.',
    delete_user_confirm: 'Delete this user?',
    activate_packager_confirm: 'Reactivate this packager? Inspectors will be able to select them again.',
    deactivate_packager_confirm: 'Deactivate this packager? They will no longer be available for new inspections, but their history will be preserved.',
    wipe_packager_confirm: 'PERMANENTLY DELETE "{name}" ({number}) and ALL their inspection data? This cannot be undone!',
    wipe_packager_final: 'Are you absolutely sure? All inspections, photos, and records for this packager will be permanently erased.',
    packager_activated: 'Packager reactivated.',
    packager_deactivated: 'Packager deactivated.',
    packager_wiped: 'Packager and all related data permanently deleted.',
    delete_wipe: 'Delete & Wipe',
    activate: 'Activate',
    cleanup_title: 'Orphaned Data Cleanup',
    cleanup_desc: 'Scan for inspection records from packers that were deleted in the past. These show up in reports without names.',
    cleanup_scan: 'Scan for Orphaned Data',
    cleanup_delete_all: 'Delete All Orphaned Data',
    cleanup_none: 'No orphaned data found. Everything is clean!',
    cleanup_found: 'Found {count} orphaned inspections from {packers} deleted packer(s):',
    cleanup_orphans_confirm: 'Permanently delete {count} orphaned inspection records and their photos? This cannot be undone!',
    cleanup_orphans_done: 'Cleaned up {count} orphaned inspections.',
    product_updated: 'Product updated!',
    product_created: 'Product created!',
    product_deactivated: 'Product deactivated.',
    user_updated: 'User updated!',
    user_created: 'User created!',
    user_deleted: 'User deleted.',
    packager_updated: 'Packager updated!',
    packager_created_msg: 'Packager created!',

    // Packager filters
    min_boxes: 'Min Boxes',
    sort_by: 'Sort By',
    clear_filters: 'Clear',
    no_packers_match: 'No packers match the selected filters',
    showing: 'Showing',
    rating_star: 'Excellent (≤3%)',
    rating_good: 'Good (≤5%)',
    rating_warning: 'Warning (≤10%)',
    rating_alert: 'Poor (>10%)',
    rating_critical: 'Critical',

    // Import
    import_packagers: 'Bulk Import from Excel',
    import_packagers_desc: 'Upload an Excel (.xlsx) or CSV file with packager_number and name columns.',
    download_template: 'Download Template',
    choose_file: 'Choose File',
    import_now: 'Import',
    importing: 'Importing...',
    imported: 'Imported',
    skipped: 'Skipped',
    total: 'Total',
    row: 'Row',
  },

  tr: {
    // Common
    app_name: 'CitrusQC',
    app_subtitle: 'Taze Meyve Kalite Kontrol',
    loading: 'Yükleniyor...',
    save: 'Kaydet',
    cancel: 'İptal',
    create: 'Oluştur',
    update: 'Güncelle',
    edit: 'Düzenle',
    delete: 'Sil',
    deactivate: 'Devre Dışı',
    confirm_delete: 'Emin misiniz?',
    success: 'Başarılı!',
    error: 'Hata',
    yes: 'Evet',
    no: 'Hayır',
    search: 'Ara',
    filter: 'Filtrele',
    apply: 'Uygula',
    export_csv: 'CSV',
    export_pdf: 'PDF',
    previous: 'Önceki',
    next: 'Sonraki',
    page_of: '/',
    active: 'Aktif',
    inactive: 'Pasif',
    status: 'Durum',
    actions: 'İşlemler',
    created: 'Oluşturulma',
    date: 'Tarih',
    all: 'Tümü',

    // Auth
    login: 'Giriş Yap',
    logout: 'Çıkış',
    username: 'Kullanıcı Adı',
    password: 'Şifre',
    enter_username: 'Kullanıcı adınızı girin',
    enter_password: 'Şifrenizi girin',
    logging_in: 'Giriş yapılıyor...',
    login_failed: 'Giriş başarısız. Lütfen tekrar deneyin.',
    invalid_credentials: 'Geçersiz kimlik bilgileri.',

    // Nav
    nav_inspect: 'Kontrol',
    nav_history: 'Geçmiş',
    nav_packer_report: 'Paketçiler',
    nav_reports: 'Raporlar',
    nav_settings: 'Ayarlar',

    // Dashboard / Inspection
    box_inspection: 'Kutu Kontrolü',
    inspector: 'Kontrolör',
    packager: 'Paketçi',
    select_packager: 'Paketçi seçin...',
    product: 'Ürün',
    select_product: 'Ürün seçin...',
    minor_defects: 'Küçük Kusurlar',
    major_defects: 'Büyük Kusurlar',
    minor_pct: 'Küçük %',
    major_pct: 'Büyük %',
    total_pct: 'Toplam %',
    notes: 'Notlar',
    notes_placeholder: 'Bulunan kusurları açıklayın...',
    photos: 'Fotoğraflar',
    photos_limit: 'en fazla 5',
    submit_inspection: 'Kontrolü Gönder',
    submitting: 'Gönderiliyor...',
    box_inspected: 'Kutu Kontrol Edildi!',
    inspection_saved: 'Kontrol başarıyla kaydedildi',
    critical_defects: 'Kritik Kusurlar',
    critical_pct: 'Kritik %',
    critical_desc: 'Çürümüş / Bozulmuş',
    minor_desc: 'Şekil, renk, kabuk izleri',
    major_desc: 'Zararlı, don, yaralanma',
    fruit: 'Meyve',
    size_code: 'Boy Kodu',
    diameter: 'Çap',
    pieces_per_box: 'Adet / Kutu',
    quality_class: 'Sınıf',
    total_pieces: 'Toplam Adet',
    total_defect: 'Toplam Kusur %',

    // Barcode Scanner
    scan_barcode: 'Barkod Tara',
    scanning: 'Taranıyor...',
    point_camera: 'Kamerayı paketçi barkoduna tutun',
    stop_scan: 'Taramayı Durdur',
    or_select_manually: 'veya manuel seçin',
    packager_not_found: 'Paketçi bulunamadı',
    packager_not_found_msg: '"{code}" barkodu kayıtlı değil. Yeni paketçi oluşturmak ister misiniz?',
    create_new_packager: 'Yeni Paketçi Oluştur',
    packager_name: 'Paketçi Adı',
    packager_number: 'Paketçi Numarası',
    enter_packager_name: 'Paketçi adını girin',
    packager_created: 'Paketçi oluşturuldu!',
    camera_error: 'Kameraya erişilemedi. Lütfen izinleri kontrol edin.',

    // My Inspections
    my_inspections: 'Kontrollerim',
    no_inspections: 'Henüz kontrol yok',
    no_inspections_desc: 'Kontrol panelinden kutu kontrolüne başlayın!',
    delete_inspection: 'Kontrolü Sil',
    delete_inspection_confirm: 'Bu kontrolü ve fotoğraflarını sil? Bu geri alınamaz.',

    // Reports
    reports_title: 'Raporlar ve Analizler',
    crunching: 'Rakamlar hesaplanıyor...',
    total_boxes: 'Toplam Kutu',
    avg_minor: 'Ort. Küçük %',
    avg_major: 'Ort. Büyük %',
    avg_critical: 'Ort. Kritik %',
    critical_found: 'Kritik Bulunan',
    critical_warning: 'KRİTİK: Çürümüş/Bozulmuş meyve tespit edildi!',
    packer_report_title: 'Paketçi Performansı',
    packer_report_hint: 'Tüm kontrolörlerden yapılan kontrolleri görmek için paketçiye tıklayın',
    recent_inspections_for: 'Son Kontroller:',
    all_inspectors_note: 'Tüm kontrolörlerin kontrolleri gösteriliyor',
    total_defects: 'Toplam Kusur',
    fruits_inspected: 'Kontrol Edilen',
    boxes_per_inspector: 'Kontrolör Başına Kutu',
    major_defects_by_fruit: 'Meyveye Göre Büyük Kusurlar',
    product_distribution: 'Ürün Dağılımı',
    all_products: 'Tüm Ürünler',
    defect_trends: 'Kusur Trendleri',
    packager_performance: 'Paketçi Performansı',
    nav_overview: 'Genel Bakış',
    nav_charts: 'Grafikler',
    nav_packagers: 'Paketçiler',
    nav_inspectors: 'Kontrolörler',
    prev: 'Önceki',
    next: 'Sonraki',
    packer_history_title: 'Paketçi Performans Geçmişi',
    packer_history_prompt: 'Günlük performans geçmişini görmek için paketçi seçin',
    daily_performance: 'Günlük Performans',
    inspector_performance: 'Kontrolör Performansı',
    boxes: 'Kutu',
    name: 'Ad',
    rating: 'Puan',
    minor_found: 'Küçük Bulunan',
    major_found: 'Büyük Bulunan',
    from_date: 'Başlangıç',
    to_date: 'Bitiş',
    period_1d: '1 Gün',
    period_1w: '1 Hafta',
    period_1m: '1 Ay',
    all_time: 'Tüm Zamanlar',
    top_packager: 'En İyi Paketçi',
    worst_packager: 'En Kötü Paketçi',
    least_active_inspector: 'En Az Aktif Kontrolör',
    top_inspector: 'En İyi Kalite Kontrolör',
    quality_controller: 'Kalite Kontrolör',

    // Settings
    settings_title: 'Ayarlar',
    products_tab: 'Ürünler',
    users_tab: 'Kullanıcılar',
    packagers_tab: 'Paketçiler',
    product_catalog: 'Ürün Kataloğu',
    add_product: '+ Ürün Ekle',
    add_user: '+ Kullanıcı Ekle',
    add_packager: '+ Paketçi Ekle',
    new_product: 'Yeni Ürün',
    edit_product: 'Ürünü Düzenle',
    new_user: 'Yeni Kullanıcı',
    edit_user: 'Kullanıcıyı Düzenle',
    new_packager: 'Yeni Paketçi',
    edit_packager: 'Paketçiyi Düzenle',
    product_code: 'Ürün Kodu',
    fruit_type: 'Meyve Türü',
    diameter_range: 'Çap Aralığı',
    description: 'Açıklama',
    minor_tolerance: 'Küçük Tolerans %',
    major_tolerance: 'Büyük Tolerans %',
    critical_tolerance: 'Kritik Tolerans %',
    tolerance_settings: 'Kusur Toleransları (OECD)',
    role: 'Rol',
    new_password: 'Yeni şifre (opsiyonel)',
    deactivate_confirm: 'Bu ürünü devre dışı bırak? Yeni kontrollerde kullanılamaz.',
    delete_user_confirm: 'Bu kullanıcıyı sil?',
    activate_packager_confirm: 'Bu paketçiyi tekrar aktif et? Kontrolörler tekrar seçebilecek.',
    deactivate_packager_confirm: 'Bu paketçiyi devre dışı bırak? Yeni kontrollerde seçilemez ama geçmişi korunur.',
    wipe_packager_confirm: '"{name}" ({number}) paketçisini ve TÜM kontrol verilerini KALICI olarak sil? Bu geri alınamaz!',
    wipe_packager_final: 'Kesinlikle emin misiniz? Bu paketçinin tüm kontrolleri, fotoğrafları ve kayıtları kalıcı olarak silinecek.',
    packager_activated: 'Paketçi tekrar aktif edildi.',
    packager_deactivated: 'Paketçi devre dışı bırakıldı.',
    packager_wiped: 'Paketçi ve tüm ilgili veriler kalıcı olarak silindi.',
    delete_wipe: 'Sil ve Temizle',
    activate: 'Aktif Et',
    cleanup_title: 'Sahipsiz Veri Temizliği',
    cleanup_desc: 'Geçmişte silinen paketçilere ait kontrol kayıtlarını tara. Bunlar raporlarda isimsiz olarak görünür.',
    cleanup_scan: 'Sahipsiz Verileri Tara',
    cleanup_delete_all: 'Tüm Sahipsiz Verileri Sil',
    cleanup_none: 'Sahipsiz veri bulunamadı. Her şey temiz!',
    cleanup_found: 'Silinen {packers} paketçiye ait {count} sahipsiz kontrol kaydı bulundu:',
    cleanup_orphans_confirm: '{count} sahipsiz kontrol kaydını ve fotoğraflarını kalıcı olarak sil? Bu geri alınamaz!',
    cleanup_orphans_done: '{count} sahipsiz kontrol kaydı temizlendi.',
    product_updated: 'Ürün güncellendi!',
    product_created: 'Ürün oluşturuldu!',
    product_deactivated: 'Ürün devre dışı bırakıldı.',
    user_updated: 'Kullanıcı güncellendi!',
    user_created: 'Kullanıcı oluşturuldu!',
    user_deleted: 'Kullanıcı silindi.',
    packager_updated: 'Paketçi güncellendi!',
    packager_created_msg: 'Paketçi oluşturuldu!',

    // Packager filters
    min_boxes: 'Min Kutu',
    sort_by: 'Sıralama',
    clear_filters: 'Temizle',
    no_packers_match: 'Seçilen filtrelere uygun paketçi bulunamadı',
    showing: 'Gösterilen',
    rating_star: 'Mükemmel (≤3%)',
    rating_good: 'İyi (≤5%)',
    rating_warning: 'Uyarı (≤10%)',
    rating_alert: 'Kötü (>10%)',
    rating_critical: 'Kritik',

    // Import
    import_packagers: 'Excel\'den Toplu İçe Aktarma',
    import_packagers_desc: 'Paketçi numarası ve ad sütunları içeren Excel (.xlsx) veya CSV dosyası yükleyin.',
    download_template: 'Şablon İndir',
    choose_file: 'Dosya Seç',
    import_now: 'İçe Aktar',
    importing: 'İçe aktarılıyor...',
    imported: 'Aktarılan',
    skipped: 'Atlanan',
    total: 'Toplam',
    row: 'Satır',
  }
};

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem('lang') || 'en');

  useEffect(() => {
    localStorage.setItem('lang', lang);
  }, [lang]);

  const t = (key, replacements = {}) => {
    let text = translations[lang]?.[key] || translations.en[key] || key;
    Object.entries(replacements).forEach(([k, v]) => {
      text = text.replace(`{${k}}`, v);
    });
    return text;
  };

  const toggleLang = () => setLang(l => l === 'en' ? 'tr' : 'en');

  return (
    <LanguageContext.Provider value={{ lang, setLang, toggleLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLang = () => useContext(LanguageContext);
